import { ApolloQueryResult } from '@apollo/client';
import { Image } from '@mallpay/orbis';
import { format } from 'date-fns';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import HeartBrainIllustration from '@skippay/core/assets/illustrations/brain_heart_sm.png';
import { Flex } from '@skippay/core/components/Flex';
import ModalContent from '@skippay/core/components/ModalContent';
import Text from '@skippay/core/components/Text';
import useCountdown from '@skippay/core/hooks/useCountdown';
import { FormattedMessage, useIntl } from '@skippay/core/i18n/intl';

import { TOKEN_EXPIRES_IN_DELAY } from 'constants/authorization';
import { sessionExpiredAction, userLogoutAction } from 'modules/auth/actions';

import { SButton } from './index.styled';

interface WarningModalProps {
	onContinue: () => Promise<ApolloQueryResult<{ id: string }>>;
	onClose: () => void;
}

const WarningModal = (props: WarningModalProps) => {
	const dispatch = useDispatch();
	const intl = useIntl();
	const { remaining = 0, running } = useCountdown({
		period: TOKEN_EXPIRES_IN_DELAY,
		startImmediately: true,
	});
	const onContinue = () => {
		props.onContinue().then(() => props.onClose());
	};

	const onTokenExpired = (logoutManualy = false) => {
		props.onClose();
		if (logoutManualy) {
			dispatch(userLogoutAction());
		}
		dispatch(sessionExpiredAction());
	};

	useEffect(() => {
		if (!running) {
			onTokenExpired();
		}
	}, [running]);

	return (
		<ModalContent
			closeBtnHandler={onContinue}
			Header={
				<Text as="h1" size="h3">
					<FormattedMessage defaultMessage="Brzy budete odhlášeni" id="LogoutWarningModal.title" />
				</Text>
			}
			testId="logoutWarning"
		>
			<Image
				alt={intl.formatMessage({
					defaultMessage: 'Ilustrace srdce a mozku',
					id: 'LogoutWarningModal.illustrationAlt',
				})}
				width={320}
				className="mx-auto -translate-x-10 sm:-translate-x-16"
				src={HeartBrainIllustration}
			/>

			<Text as="strong" className="mt-8! block">
				<FormattedMessage defaultMessage="Jste tu ještě?" id="LogoutWarningModal.subtitle" />
			</Text>

			<Text className="mt-4!">
				<FormattedMessage
					defaultMessage="Všimli jsme si, že už nějakou chvilku nejste u nás v Zákaznické zóně aktivní, proto vás za minutku pro jistotu automaticky odhlásíme."
					id="LogoutWarningModal.description"
				/>
			</Text>

			<Text className="mt-8!">
				<FormattedMessage
					defaultMessage="Na potvrzení zbývá: <strong>{remainingSeconds}</strong>"
					id="LogoutWarningModal.automaticLogoutCountdown"
					values={{
						remainingSeconds: format(remaining, 'mm:ss'),
						strong: (title) => <Text as="strong">{title}</Text>,
					}}
				/>
			</Text>

			<Flex variant={['column', 'row']} gap={2} justify="center" className="mt-8!">
				<SButton
					variant="secondary"
					onClick={() => onTokenExpired(true)}
					analyticsId="logout"
					testId="logout"
				>
					<FormattedMessage defaultMessage="Odhlásit se" id="LogoutWarningModal.button.logout" />
				</SButton>
				<SButton onClick={onContinue} analyticsId="stay" testId="stay">
					<FormattedMessage
						defaultMessage="Zůstat na stránce"
						id="LogoutWarningModal.button.stay"
					/>
				</SButton>
			</Flex>
		</ModalContent>
	);
};

export default WarningModal;
